import React, { useContext, useMemo } from "react";

import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { Link as RouterLink } from "react-router-dom";

import { makeStyles } from "@material-ui/styles";

import clsx from "clsx";

import { WarningOutlined } from "@material-ui/icons";

import { Alert } from "@material-ui/lab";

import FpLogoLight from "../../components/FpLogoLight";
import DuplicateTrackerWarningIcon, {
  useDuplicateTrackerWarningBackgroundColor,
} from "./DuplicateTrackerWarningIcon";
import useFeatureFlags from "../../hooks/useFeatureFlag";
import useAllSaasDestinationsByType from "../../hooks/useAllSaasDestinationsByType";
import DestinationLogo from "./DestinationLogo";
import useWTMPageStyles from "./useWTMPageStyles";
import { OverviewDataContext } from "../OverviewDataContextProvider";
import ColumnLabelWithTooltip from "../ColumnLabelWithTooltip";
import { ShieldCheckmarkIcon } from "../../components/icons/themes/windows10";
import { isDestinationInHIPAAMode } from "../../util";
import { LoadedDestinationInfo } from "../../types/global";
import useWTMURLs from "../useWTMURLs";
import { allowListProps } from "./PHIAllowListTab";
import { AllowListProp } from "./PHIAllowListTab/PropsFilterSelect";

const useStyles = makeStyles((theme) => ({
  hipaaModeWrapper: {
    borderRadius: "50%",
    display: "inline-block",
    height: "2em",
    position: "relative" as "relative",
    textAlign: "center" as "center",
    width: "2em",
  },
  hipaaModeDisabledWrapper: {
    background: theme.palette.warning.main,
  },
  hipaaModeEnabledWrapper: {
    background: theme.palette.success.main,
  },
  hipaaModeIcon: {
    left: "50%",
    position: "absolute" as "absolute",
    top: "50%",
    transform: "translateX(-50%) translateY(-50%)",
    "& path": {
      stroke: "white",
      strokeWidth: 5,
    },
  },
}));

function allAllowListProps(
  d: LoadedDestinationInfo
): {
  eventProps: AllowListProp[];
  userProps: AllowListProp[];
  groupProps: AllowListProp[];
} {
  const eventProps = allowListProps(
    d,
    d.event_property_sensitivity_levels,
    "event"
  );
  const userProps = allowListProps(
    d,
    d.user_property_sensitivity_levels,
    "user"
  );
  const groupProps = allowListProps(
    d,
    d.group_property_sensitivity_levels,
    "group"
  );

  return { eventProps, userProps, groupProps };
}

type PHIAllowListDescriptionTableCellProps = {
  destination: LoadedDestinationInfo;
};

function PHIAllowListDescriptionTableCell({
  destination,
}: PHIAllowListDescriptionTableCellProps) {
  const wtmURLs = useWTMURLs();

  const { eventProps, userProps, groupProps } = allAllowListProps(destination);

  const totalNumProps =
    eventProps.length + userProps.length + groupProps.length;

  if (wtmURLs.phiAllowlist === undefined) {
    return null;
  }

  return (
    <TableCell>
      <RouterLink
        to={`${wtmURLs.phiAllowlist}?destination=${destination.name}`}
      >
        <Tooltip
          interactive
          title={
            <>
              <Box marginBottom="1em">
                Manage the PHI Allowlist for <strong>{destination.name}</strong>
                .
              </Box>

              <Box>
                Currently allowing {eventProps.length} event properties,{" "}
                {userProps.length} user properties, and {groupProps.length}{" "}
                group properties.
              </Box>
            </>
          }
          arrow
        >
          <Link>{totalNumProps} properties allowed.</Link>
        </Tooltip>
      </RouterLink>
    </TableCell>
  );
}

type HIPAADisabledIconProps = {
  destination: LoadedDestinationInfo;
};

function HIPAADisabledIcon({ destination }: HIPAADisabledIconProps) {
  const classes = useStyles();

  return (
    <Tooltip
      title={`ID masking and Allowlisting disabled. PHI may be shared with ${destination.name}. If your organization does not have a BAA with ${destination.name}, you may want to turn on ID masking and allowlisting in order to govern what PHI is shared with ${destination.name}.`}
      arrow
    >
      <span
        className={clsx(
          classes.hipaaModeWrapper,
          classes.hipaaModeDisabledWrapper
        )}
      >
        <WarningOutlined
          fontSize="small"
          htmlColor="white"
          style={{ marginTop: "2px" }}
        />
      </span>
    </Tooltip>
  );
}

function HIPAAEnabledIcon() {
  const classes = useStyles();

  return (
    <Tooltip title="ID masking and Allowlisting enabled." arrow>
      <span
        className={clsx(
          classes.hipaaModeWrapper,
          classes.hipaaModeEnabledWrapper
        )}
      >
        <ShieldCheckmarkIcon className={classes.hipaaModeIcon} color="white" />
      </span>
    </Tooltip>
  );
}

function ActiveDestinationsTable() {
  const wtmPageClasses = useWTMPageStyles();
  const wtmURLs = useWTMURLs();
  const flags = useFeatureFlags();
  const duplicateTrackerWarningBackgroundColor = useDuplicateTrackerWarningBackgroundColor();

  const {
    activeDestinations,
    isLoadingActiveDestinations,
    potentialDuplicateTrackerNames,
    trackerNameHovered,
    setTrackerNameHovered,
  } = useContext(OverviewDataContext);

  const allSaasDestinationsByKey = useAllSaasDestinationsByType();

  const handleMouseEnter = (name: string) => {
    if (!potentialDuplicateTrackerNames.has(name)) {
      return;
    }

    setTrackerNameHovered(name);
  };

  const handleMouseLeave = (name: string) => {
    if (name !== trackerNameHovered) {
      return;
    }

    setTrackerNameHovered(null);
  };

  const numDestinationsNotInHIPAAMode = useMemo(() => {
    return activeDestinations.filter((d) => !isDestinationInHIPAAMode(d))
      .length;
  }, [activeDestinations]);

  if (!flags("unifiedDestinationsUI") || activeDestinations.length === 0) {
    return null;
  }

  return (
    <Card>
      <CardHeader
        className={wtmPageClasses.cardHeader}
        title={
          <>
            <Typography variant="h5">Active Freshpaint Destinations</Typography>
            {numDestinationsNotInHIPAAMode > 0 && (
              <Alert
                severity="warning"
                style={{ marginTop: "1em", outline: "1px solid #eee" }}
              >
                <Typography variant="body1">
                  {numDestinationsNotInHIPAAMode} out of{" "}
                  {activeDestinations.length} of your active destinations are
                  not in HIPAA mode.{" "}
                  <Link
                    href="https://documentation.freshpaint.io/integrations/hipaa-mode"
                    target="_blank"
                  >
                    Learn more about HIPAA mode.
                  </Link>
                </Typography>
              </Alert>
            )}
          </>
        }
      />
      <CardContent className={wtmPageClasses.cardContent}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>
                  <ColumnLabelWithTooltip
                    label="Destination"
                    tooltip="An active Freshpaint destination or tool"
                  />
                </TableCell>

                <TableCell align="center">
                  <ColumnLabelWithTooltip
                    label="HIPAA Mode Enabled?"
                    tooltip="When HIPAA mode is enabled, Freshpaint will mask identifiers and restrict the properties sent to this destination to only those in the PHI Allowlist."
                  />
                </TableCell>

                {wtmURLs.phiAllowlist !== undefined && (
                  <TableCell align="left">
                    <ColumnLabelWithTooltip
                      label="PHI Allowlist"
                      tooltip="When HIPAA protections are enabled, the PHI Allowlist controls which properties are allowed to be sent to your Freshpaint destinations."
                    />
                  </TableCell>
                )}

                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoadingActiveDestinations ? (
                <TableRow>
                  <TableCell colSpan={1}>Loading...</TableCell>
                </TableRow>
              ) : (
                activeDestinations.map((d) => (
                  <TableRow
                    key={`destination-row-${d.name}`}
                    onMouseEnter={() => handleMouseEnter(d.name)}
                    onMouseLeave={() => handleMouseLeave(d.name)}
                    style={{
                      background: duplicateTrackerWarningBackgroundColor(
                        d.name,
                        trackerNameHovered
                      ),
                    }}
                  >
                    <TableCell>
                      <RouterLink
                        to={`/destinations/apps/${
                          allSaasDestinationsByKey[d.name]?.slug
                        }/${d.id}`}
                      >
                        <DestinationLogo destinationName={d.name} />
                        {d.name}
                      </RouterLink>

                      <Tooltip title="This is an active Freshpaint destination.">
                        <FpLogoLight
                          display="inline-block"
                          height="1em"
                          style={{
                            marginLeft: "0.5em",
                          }}
                          viewBox="0 0 125 125"
                          width="1em"
                        />
                      </Tooltip>

                      <DuplicateTrackerWarningIcon
                        potentialDuplicateTrackerNames={
                          potentialDuplicateTrackerNames
                        }
                        destinationKey={d.name}
                      />
                    </TableCell>

                    <TableCell align="center">
                      {isDestinationInHIPAAMode(d) ? (
                        <HIPAAEnabledIcon />
                      ) : (
                        <HIPAADisabledIcon destination={d} />
                      )}
                    </TableCell>

                    <PHIAllowListDescriptionTableCell destination={d} />

                    <TableCell align="right">
                      {allSaasDestinationsByKey[d.name] !== undefined && (
                        <RouterLink
                          to={`/destinations/apps/${
                            allSaasDestinationsByKey[d.name]?.slug
                          }/${d.id}`}
                        >
                          <Button
                            color="primary"
                            size="small"
                            variant="outlined"
                          >
                            Manage
                          </Button>
                        </RouterLink>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
}

export default ActiveDestinationsTable;
